import React, { createContext, useContext, useState, useEffect } from "react";
import axios, { AxiosError } from "axios";
import { API_BASE_URL } from "../constants";
import { GameLog, PlayLog } from "../types/StatsTypes";
import { DepthChart, DepthChartPlayer, DepthChartPlayerAssignment, DepthChartPosition } from "../types/DepthChartTypes";
import { useDepthChart } from "./DepthChartContext";

export interface TeamComparisonStats {
    teamName: string;
    opponentName: string;
    categories: TeamComparisonStatsCategory[];
}

export interface TeamComparisonStatsCategory {
    category: string;
    stats: Stats[];
}

export interface Stats {
    label: string;
    team: string;
    opponent: string;
}

interface StatsContextType {
    playLogs: PlayLog[];
    setPlayLogs: (playLogs: PlayLog[]) => void;
    gameLog: GameLog | null;
    setGameLog: (gameLog: GameLog) => void;
    gameStats: TeamComparisonStats | null;
    isLoadingStats: boolean;
    statsError: string | null;
    fetchGameStats: (gameId: number, clubId: number) => Promise<void>;
}


const StatsContext = createContext<StatsContextType | undefined>(undefined);


export const StatsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [playLogs, setPlayLogs] = useState<PlayLog[]>([]);
    const [gameLog, setGameLog] = useState<GameLog | null>(null);
    const [gameStats, setGameStats] = useState<TeamComparisonStats | null>(null);
    const [isLoadingStats, setIsLoadingStats] = useState(false);
    const [statsError, setStatsError] = useState<string | null>(null);

    const fetchGameStats = async (gameId: number, clubId: number) => {
        setIsLoadingStats(true);
        setStatsError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/stats/get_game_stats/`, {
                params: {
                    game_id: gameId,
                    club_id: clubId,
                },
            });
            setGameStats(response.data);
        } catch (err: any) {
            setStatsError(err.message || "An error occurred while fetching game stats");
            console.log("Error fetching game stats:", err);
        } finally {
            setIsLoadingStats(false);
        }
    };

    return (
        <StatsContext.Provider 
        value={{
            playLogs,
            setPlayLogs,
            gameLog,
            setGameLog,
            gameStats,
            isLoadingStats,
            statsError,
            fetchGameStats,
        }}
        >
            {children}
        </StatsContext.Provider>
    );
};


export const useStats = () => {
    const context = useContext(StatsContext);
    if (context === undefined) {
      throw new Error("useStats must be used within a StatsProvider");
    }
    return context;
  };